import React, { Ref } from 'react';

import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';

import { ClickableContainer } from 'components/clickable-container';
import { IStoreDiagnosticData } from 'components/store-card-icons/internal-store-diagnostic-modal';
import StoreInfoModalContainer from 'components/store-info-modal';
import { useStoreCard } from 'hooks/store-card';
import useMediaQuery from 'hooks/use-media-query';
import { useGeolocation } from 'state/geolocation';
import { useUIContext } from 'state/ui';
import { nextOpenDay, readableHour } from 'utils/restaurant';
import { routes } from 'utils/routing';

import DefaultHoursText from './default-hours-text';
import StoreCardFooter from './store-card-footer';
import { messages } from './store-card-messages';
import {
  AddressHeader,
  AddressSubheader,
  DirectionsLinkStyled,
  Divider,
  MoreInfoLink,
  SelectedBorderVertical,
  StoreCardContainer,
  SubheaderContainer,
  TimeNote,
  TimeNoteWrapper,
} from './styled';
import { AvailableServiceModesText } from './styled.th';
import { IStoreCardProps } from './types';

const StoreCard = React.forwardRef(
  (
    {
      Footer = StoreCardFooter,
      HoursText = DefaultHoursText,
      className,
      isSelected,
      onClick,
      isListView = false,
      restaurant,
    }: IStoreCardProps,
    ref: Ref<HTMLDivElement>
  ) => {
    const {
      storeSEOSlug,
      distanceText,
      closeHourTodayText,
      curbsideOpen,
      dineInOpen,
      takeoutOpen,
      diningRoomOpen,
      driveThruOpen,
      openHours,
      selectRestaurant,
      isRestaurantOpen,
      isMobileOrderingAvailable,
      isRestaurantPosOnline,
      areDiagnosticToolsEnabled,
    } = useStoreCard(restaurant);
    const { formatMessage } = useIntl();
    const { activeCoordinates } = useGeolocation();
    const position = { lat: restaurant.latitude, lng: restaurant.longitude };
    const isDesktop = useMediaQuery('desktop');
    const { hideStoreInfoDialog, showStoreInfoDialog, isShowingStoreInfoDialog } = useUIContext();
    const { pathname } = useLocation();
    const navigate = useNavigate();

    if (!restaurant || !restaurant.number) {
      return null;
    }

    // There are two places where this card appears
    // 1. The Store locator store-locator - Soon to be deprecated by store-card-slim usage here
    // 2. The order confirmation page order-confirmation
    //
    // We want to keep the store-locator/store/:id link because it can be used for marketing/direct linking
    // However, we don't wont to link the user away from the order confirmation page, so in
    // this instance we want to use a dialog
    const onConfirmationPage: boolean =
      (pathname && pathname.startsWith(routes.orderConfirmation)) || false;

    // This is the same as BK and PLK's moreInfoRoute on <StoreCardFooter/>
    // Relative canonical urls are technically permitted.
    // However, because the code sometimes stacks relative urls, be careful!
    const moreInfoRoute = `${routes.store}/${storeSEOSlug}`;

    const moreInfoOnClick = () => {
      onConfirmationPage ? showStoreInfoDialog() : navigate(moreInfoRoute);
    };

    const storeDiagnosticData: IStoreDiagnosticData = {
      driveThruOpen,
      diningRoomOpen,
      isRestaurantOpen,
      isMobileOrderingAvailable,
      isRestaurantPosOnline,
      restaurantName: restaurant.name || '',
      restaurantHasMobileOrdering: !!restaurant.hasMobileOrdering,
      restaurantCountry: restaurant?.physicalAddress?.country || '',
      restaurantStreetAddress: restaurant?.physicalAddress?.address1 || '',
    };

    return (
      <>
        {onConfirmationPage && isShowingStoreInfoDialog && (
          <StoreInfoModalContainer
            // Do NOT show the canonical URL in this case.
            // In the future, avoid this by creating a real route for store info
            disableCanonical
            showOrderCTA={false}
            storeId={restaurant._id}
            onDismiss={hideStoreInfoDialog}
          />
        )}
        <ClickableContainer className={className} onClick={onClick} ref={ref}>
          <StoreCardContainer isListView={isListView}>
            {isSelected && <SelectedBorderVertical />}
            <AddressHeader>
              <h2 data-testid="restaurant-address">
                {restaurant?.physicalAddress?.address1 || ''}
                {isListView && isDesktop && (
                  <DirectionsLinkStyled activeCoordinates={activeCoordinates} position={position} />
                )}
              </h2>
              <p>{distanceText}</p>
            </AddressHeader>
            {isListView && !isDesktop && (
              <DirectionsLinkStyled activeCoordinates={activeCoordinates} position={position} />
            )}
            <SubheaderContainer>
              <AddressSubheader>
                <AvailableServiceModesText>
                  {[
                    dineInOpen && formatMessage(messages.dineIn),
                    takeoutOpen && formatMessage(messages.takeOut),
                    driveThruOpen && formatMessage(messages.driveThru),
                    curbsideOpen && formatMessage(messages.curbside),
                  ]
                    .filter(Boolean)
                    .join(', ')}
                </AvailableServiceModesText>
                <MoreInfoLink
                  onClick={moreInfoOnClick}
                  aria-label={formatMessage(messages.storeInfoAssistive)}
                >
                  {formatMessage(messages.storeInfo)}
                </MoreInfoLink>
              </AddressSubheader>
            </SubheaderContainer>
            <TimeNoteWrapper>
              <TimeNote open={driveThruOpen || diningRoomOpen}>
                <p>
                  <HoursText
                    closeHourToday={closeHourTodayText}
                    isOpen={driveThruOpen || diningRoomOpen}
                    nextOpenHour={readableHour(nextOpenDay(openHours))}
                  />
                </p>
              </TimeNote>
            </TimeNoteWrapper>
            <Divider />
            <Footer
              restaurant={restaurant}
              selectRestaurant={selectRestaurant}
              isRestaurantSelectable={isRestaurantOpen}
              areStoreCardDiagnosticsEnabled={areDiagnosticToolsEnabled}
              storeDiagnosticData={storeDiagnosticData}
            />
          </StoreCardContainer>
        </ClickableContainer>
      </>
    );
  }
);

export default StoreCard;
